// @ts-strict-ignore
import { InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { isNil } from 'lodash';
import { useCallback, useContext } from 'react';
import { Mutations, Queries } from '@shared/api';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { useTagOptions } from '@shared/components/blocks/lib/tags/hooks';
import { MAX_TAG_LENGTH, generateTagColor } from '@shared/components/blocks/lib/tags/utils';
import { API } from '@shared/types/api';

export function useCreateCardTag(
  fieldName: string,
): (label: string) => Promise<API.TagOptionFragment> {
  const channel = useContext(CardChannelContext);
  const { options } = useTagOptions(fieldName);
  const [createStringTag] = useMutation<
    API.CreateCardFieldStringTagMutation,
    API.CreateCardFieldStringTagMutationVariables
  >(Mutations.cards.createCardFieldStringTag);
  return useCallback(
    async (label: string) => {
      const res = await createStringTag({
        awaitRefetchQueries: true,
        refetchQueries: getRefetchQueries(channel?.id, channel?.workflowTemplateId),
        variables: {
          input: {
            fieldName,
            channelId: channel?.id,
            colorName: generateTagColor(options.length),
            label: label.substr(0, MAX_TAG_LENGTH),
            sortKey: String(Date.now()),
          },
        },
      });
      return res.data?.tag;
    },
    [createStringTag, channel?.id, channel?.workflowTemplateId, fieldName, options.length],
  );
}

export function useUpdateCardTag(): (id: string, label: string) => Promise<API.TagOptionFragment> {
  const channel = useContext(CardChannelContext);
  const [updateStringTag] = useMutation<
    API.UpdateCardFieldStringTagMutation,
    API.UpdateCardFieldStringTagMutationVariables
  >(Mutations.cards.updateCardFieldStringTag);
  return useCallback(
    async (id: string, label: string) => {
      const res = await updateStringTag({
        awaitRefetchQueries: true,
        refetchQueries: getRefetchQueries(channel?.id, channel?.workflowTemplateId),
        variables: {
          input: { id, label },
        },
      });
      return res.data?.tag;
    },
    [updateStringTag, channel?.id, channel?.workflowTemplateId],
  );
}

export function useArchiveCardTag(): (
  id: string,
  revert: boolean,
) => Promise<API.TagOptionFragment> {
  const channel = useContext(CardChannelContext);
  const [archiveStringTag] = useMutation<
    API.ArchiveCardFieldStringTagMutation,
    API.ArchiveCardFieldStringTagMutationVariables
  >(Mutations.cards.archiveCardFieldStringTag);
  return useCallback(
    async (id: string, revert: boolean) => {
      const res = await archiveStringTag({
        awaitRefetchQueries: true,
        refetchQueries: getRefetchQueries(channel?.id, channel?.workflowTemplateId),
        variables: {
          input: { id, revert },
        },
      });
      return res.data?.tag;
    },
    [archiveStringTag, channel?.id, channel?.workflowTemplateId],
  );
}

function getRefetchQueries(
  channelId: number | undefined,
  workflowTemplateId: string | null | undefined,
): InternalRefetchQueriesInclude {
  const queries: InternalRefetchQueriesInclude = [
    {
      query: Queries.card.listCardFieldStringTags,
      variables: {
        input: { channelId },
        skip: isNil(channelId),
      },
    },
  ];
  if (!isNil(workflowTemplateId)) {
    queries.push({
      query: Queries.workflow.getWorkflowTemplate,
      variables: { id: workflowTemplateId },
    });
  }
  return queries;
}
